import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import SquareIntegrationCard from '../components/SquareIntegrationCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Building2, 
  MapPin, 
  Users, 
  Truck, 
  Coffee,
  Plus,
  Settings2,
  Save,
  Pencil,
  AlertTriangle,
  Trash2,
  Clock,
  Receipt,
  Send,
  Link,
  Unlink,
  Copy,
  Check,
  Loader2,
  Mail,
  UserPlus,
  UserMinus,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { useModules } from '../context/ModulesContext';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const DEFAULT_HOURS = { open: '09:00', close: '17:00' };

const SettingsPage = () => {
  const { currentLocation, api, user, fetchLocations, locations, setCurrentLocation } = useAuth();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  
  // Form states
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [orgPosProvider, setOrgPosProvider] = useState('toast');
  const [savingPosProvider, setSavingPosProvider] = useState(false);
  const [providerCatalog, setProviderCatalog] = useState(null);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', role: '', hourly_rate: '' });
  const [newSupplier, setNewSupplier] = useState({ name: '', category: '', contact_email: '' });
  
  // Edit location state
  const [editLocationDialogOpen, setEditLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editLocationName, setEditLocationName] = useState('');
  const [editLocationAddress, setEditLocationAddress] = useState('');
  const [editOpeningHours, setEditOpeningHours] = useState({});

  // Reset data state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Invoice processing config
  const [invoiceConfig, setInvoiceConfig] = useState({ auto_spread_delivery: true });
  const [savingInvoiceConfig, setSavingInvoiceConfig] = useState(false);

  // Tip split config (org-level)
  const [tipSplit, setTipSplit] = useState({ mode: 'by_logged_user' });
  const [savingTipSplit, setSavingTipSplit] = useState(false);

  // Telegram connection state
  const [telegramStatus, setTelegramStatus] = useState({ connected: false, telegram_display_name: null });
  const [telegramLoading, setTelegramLoading] = useState(true);
  const [linkCode, setLinkCode] = useState(null);
  const [codeExpiry, setCodeExpiry] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Telegram notification preferences
  const [notifPrefs, setNotifPrefs] = useState({
    weekly_report: true,
    document_expiry_alerts: true,
    expiry_alert_days: 14,
    fixed_cost_alerts: true,
  });
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Feature flags
  const [features, setFeatures] = useState({
    board_meeting: true, ai_projects: true, ai_chat: true, smart_invoice: true, email: true,
  });
  const [featuresLoading, setFeaturesLoading] = useState(false);

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('staff');
  const [addingMember, setAddingMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [currentLocation]);
  
  // Fetch invoice config when location changes
  useEffect(() => {
    if (currentLocation) {
      api.get(`/invoice-config/${currentLocation.id}`)
        .then(res => setInvoiceConfig(res.data))
        .catch(() => setInvoiceConfig({ auto_spread_delivery: true }));
    }
  }, [currentLocation, api]);

  // Fetch tip-split config (org-level, no location dep)
  useEffect(() => {
    api.get('/tip-split-config')
      .then(res => setTipSplit(res.data))
      .catch(() => setTipSplit({ mode: 'by_logged_user' }));
  }, [api]);

  const handleTipSplitChange = async (newMode) => {
    setSavingTipSplit(true);
    try {
      await api.put('/tip-split-config', { mode: newMode });
      setTipSplit({ mode: newMode });
      toast.success('Tip split mode updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update tip split');
    } finally {
      setSavingTipSplit(false);
    }
  };

  // Fetch Telegram connection status + preferences
  useEffect(() => {
    setTelegramLoading(true);
    api.get('/settings/telegram')
      .then(res => {
        setTelegramStatus(res.data);
        if (res.data.connected) {
          api.get('/settings/telegram/preferences')
            .then(prefRes => setNotifPrefs(prev => ({ ...prev, ...prefRes.data })))
            .catch(() => {});
        }
      })
      .catch(() => setTelegramStatus({ connected: false }))
      .finally(() => setTelegramLoading(false));
  }, [api]);

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (!codeExpiry) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((new Date(codeExpiry) - new Date()) / 1000));
      if (remaining <= 0) {
        setLinkCode(null);
        setCodeExpiry(null);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [codeExpiry]);

  // Fetch feature flags
  useEffect(() => {
    api.get('/features').then(res => setFeatures(res.data)).catch(() => {});
  }, [api]);

  const handleToggleFeature = async (key, val) => {
    setFeaturesLoading(true);
    try {
      const res = await api.put('/features', { [key]: val });
      setFeatures(res.data);
      toast.success(`Feature ${val ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update'); }
    finally { setFeaturesLoading(false); }
  };


  const handleGenerateTelegramCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await api.post('/settings/telegram/generate-code');
      setLinkCode(res.data.code);
      setCodeExpiry(new Date(Date.now() + res.data.expires_in_seconds * 1000).toISOString());
      setCodeCopied(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCommand = () => {
    navigator.clipboard.writeText(`/connect ${linkCode}`);
    setCodeCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleDisconnectTelegram = async () => {
    setDisconnecting(true);
    try {
      await api.delete('/settings/telegram/disconnect');
      setTelegramStatus({ connected: false, telegram_display_name: null });
      setLinkCode(null);
      setCodeExpiry(null);
      toast.success('Telegram disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleToggleNotifPref = async (key, value) => {
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    setSavingPrefs(true);
    try {
      await api.put('/settings/telegram/preferences', updated);
    } catch (err) {
      toast.error('Failed to save preference');
      setNotifPrefs(notifPrefs);
    } finally {
      setSavingPrefs(false);
    }
  };

  // Team management
  const fetchTeamMembers = async () => {
    setTeamLoading(true);
    try {
      const res = await api.get('/users');
      setTeamMembers(res.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!addMemberEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setAddingMember(true);
    try {
      const res = await api.post('/team/add', { email: addMemberEmail.trim(), role: addMemberRole });
      if (res.data.status === 'not_found') {
        toast.error(res.data.message);
      } else {
        toast.success(res.data.message);
        setAddMemberEmail('');
        setAddMemberRole('staff');
        fetchTeamMembers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add team member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    setRemovingMemberId(memberId);
    try {
      const res = await api.delete(`/team/members/${memberId}`);
      toast.success(res.data.message);
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await api.put(`/users/${memberId}/role`, { user_id: memberId, role: newRole });
      toast.success('Role updated');
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const ROLE_LABELS = {
    owner: 'Owner',
    manager: 'Manager',
    staff: 'Staff',
    viewer: 'Viewer',
  };

  const ROLE_COLORS = {
    owner: 'bg-amber-100 text-amber-800 border-amber-200',
    manager: 'bg-blue-100 text-blue-800 border-blue-200',
    staff: 'bg-slate-100 text-slate-800 border-slate-200',
    viewer: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, empRes, supRes, menuRes, provRes] = await Promise.all([
        api.get('/organizations'),
        currentLocation ? api.get('/employees', { params: { location_id: currentLocation.id } }) : Promise.resolve({ data: [] }),
        currentLocation ? api.get('/suppliers', { params: { location_id: currentLocation.id } }) : Promise.resolve({ data: [] }),
        currentLocation ? api.get('/menu-items', { params: { location_id: currentLocation.id } }) : Promise.resolve({ data: [] }),
        api.get('/imports/providers').catch(() => ({ data: null })),
      ]);
      
      const org = orgRes.data[0];
      setOrganization(org);
      setOrgName(org?.name || '');
      setOrgDescription(org?.description || '');
      setOrgPosProvider(org?.pos_provider || 'toast');
      setProviderCatalog(provRes.data);
      setEmployees(empRes.data);
      setSuppliers(supRes.data);
      setMenuItems(menuRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrg = async () => {
    try {
      await api.put(`/organizations/${organization.id}`, {
        name: orgName,
        description: orgDescription
      });
      toast.success('Organization updated');
    } catch (err) {
      console.error('Error updating organization:', err);
      toast.error('Failed to update organization');
    }
  };

  const handleUpdateOrgPosProvider = async (provider) => {
    if (!organization) return;
    setSavingPosProvider(true);
    try {
      await api.put(`/organizations/${organization.id}/pos-provider`, { pos_provider: provider });
      setOrgPosProvider(provider);
      toast.success(`Default POS set to ${providerCatalog?.providers?.[provider]?.name || provider}`);
      fetchLocations();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update POS provider');
    } finally {
      setSavingPosProvider(false);
    }
  };

  const handleUpdateLocationPosProvider = async (locationId, provider) => {
    setSavingPosProvider(true);
    try {
      await api.put(`/locations/${locationId}/pos-provider`, { pos_provider: provider || null });
      toast.success(provider
        ? `Location override set to ${providerCatalog?.providers?.[provider]?.name || provider}`
        : 'Using org default');
      fetchLocations();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update location POS provider');
    } finally {
      setSavingPosProvider(false);
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocationName.trim() || !newLocationAddress.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await api.post('/locations', {
        name: newLocationName,
        address: newLocationAddress,
        organization_id: organization.id
      });
      toast.success('Location created');
      setLocationDialogOpen(false);
      setNewLocationName('');
      setNewLocationAddress('');
      fetchLocations();
    } catch (err) {
      console.error('Error creating location:', err);
      toast.error('Failed to create location');
    }
  };

  const handleCreateEmployee = async () => {
    if (!newEmployee.name.trim()) {
      toast.error('Please enter employee name');
      return;
    }

    try {
      await api.post('/employees', {
        location_id: currentLocation.id,
        name: newEmployee.name,
        role: newEmployee.role || null,
        hourly_rate: newEmployee.hourly_rate ? parseFloat(newEmployee.hourly_rate) : null
      });
      toast.success('Employee added');
      setEmployeeDialogOpen(false);
      setNewEmployee({ name: '', role: '', hourly_rate: '' });
      fetchData();
    } catch (err) {
      console.error('Error creating employee:', err);
      toast.error('Failed to add employee');
    }
  };

  const handleCreateSupplier = async () => {
    if (!newSupplier.name.trim()) {
      toast.error('Please enter supplier name');
      return;
    }

    try {
      await api.post('/suppliers', {
        location_id: currentLocation.id,
        name: newSupplier.name,
        category: newSupplier.category || null,
        contact_email: newSupplier.contact_email || null
      });
      toast.success('Supplier added');
      setSupplierDialogOpen(false);
      setNewSupplier({ name: '', category: '', contact_email: '' });
      fetchData();
    } catch (err) {
      console.error('Error creating supplier:', err);
      toast.error('Failed to add supplier');
    }
  };

  const handleEditLocation = (loc) => {
    setEditingLocation(loc);
    setEditLocationName(loc.name);
    setEditLocationAddress(loc.address || '');
    // Initialize opening hours from location or with defaults
    setEditOpeningHours(loc.opening_hours || {});
    setEditLocationDialogOpen(true);
  };

  const handleToggleAutoSpreadDelivery = async (checked) => {
    if (!currentLocation) return;
    setSavingInvoiceConfig(true);
    try {
      await api.put(`/invoice-config/${currentLocation.id}`, { auto_spread_delivery: checked });
      setInvoiceConfig(prev => ({ ...prev, auto_spread_delivery: checked }));
      toast.success(`Auto-spread delivery ${checked ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update invoice settings');
    } finally {
      setSavingInvoiceConfig(false);
    }
  };

  const toggleDayOpen = (day) => {
    setEditOpeningHours(prev => {
      if (prev[day]) {
        // Remove the day (close it)
        const newHours = { ...prev };
        delete newHours[day];
        return newHours;
      } else {
        // Add the day with default hours
        return { ...prev, [day]: { ...DEFAULT_HOURS } };
      }
    });
  };

  const updateDayHours = (day, field, value) => {
    setEditOpeningHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleUpdateLocation = async () => {
    if (!editLocationName.trim()) {
      toast.error('Please enter location name');
      return;
    }

    try {
      await api.put(`/locations/${editingLocation.id}`, {
        name: editLocationName,
        address: editLocationAddress,
        opening_hours: editOpeningHours
      });
      toast.success('Location updated');
      setEditLocationDialogOpen(false);
      setEditingLocation(null);
      fetchLocations();
      // Update current location if it was edited
      if (currentLocation?.id === editingLocation.id) {
        setCurrentLocation({
          ...currentLocation,
          name: editLocationName,
          address: editLocationAddress,
          opening_hours: editOpeningHours
        });
      }
    } catch (err) {
      console.error('Error updating location:', err);
      toast.error('Failed to update location');
    }
  };

  const handleResetData = async () => {
    if (resetConfirmation !== 'RESET ALL DATA') {
      toast.error('Please type "RESET ALL DATA" exactly to confirm');
      return;
    }

    setResetting(true);
    try {
      const res = await api.post('/settings/reset-data', null, {
        params: {
          location_id: currentLocation.id,
          confirmation: resetConfirmation
        }
      });
      toast.success(res.data.message);
      setResetDialogOpen(false);
      setResetConfirmation('');
      // Refresh the page data
      fetchData();
    } catch (err) {
      console.error('Error resetting data:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset data');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your organization, locations, and team</p>
      </div>

      <Tabs
        defaultValue={
          typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('square')
            ? 'integrations'
            : 'organization'
        }
        data-testid="settings-tabs"
      >
        <TabsList>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="team" data-testid="team-tab" onClick={() => { if (teamMembers.length === 0) fetchTeamMembers(); }}>Team</TabsTrigger>
          <TabsTrigger value="telegram" data-testid="telegram-tab">Telegram</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="menu">Menu Items</TabsTrigger>
          <TabsTrigger value="features" data-testid="features-tab">Features</TabsTrigger>
          <TabsTrigger value="modules" data-testid="modules-tab">Modules</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="integrations-tab">Integrations</TabsTrigger>
        </TabsList>

        {/* Organization Tab */}
        <TabsContent value="organization" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Details
              </CardTitle>
              <CardDescription>
                Update your organization information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  data-testid="org-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-description">Description</Label>
                <Input
                  id="org-description"
                  value={orgDescription}
                  onChange={(e) => setOrgDescription(e.target.value)}
                  placeholder="Brief description of your organization"
                  data-testid="org-description-input"
                />
              </div>
              <Button onClick={handleUpdateOrg} data-testid="save-org-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* POS Provider (org default) */}
          <Card data-testid="pos-provider-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Default POS Provider
              </CardTitle>
              <CardDescription>
                Which point-of-sale system you use for sales, orders and product mix imports.
                Individual locations can override this in the <em>Locations</em> tab.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {providerCatalog ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.values(providerCatalog.providers).map((p) => {
                    const active = orgPosProvider === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => handleUpdateOrgPosProvider(p.id)}
                        disabled={savingPosProvider}
                        className={`text-left p-3 rounded-md border transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                        data-testid={`org-provider-option-${p.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium" style={{ color: p.colour }}>{p.name}</div>
                          {active && <Badge className="text-[10px]">Default</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {p.id === 'toast' && 'ZIP / CSV exports from Toast Dashboard.'}
                          {p.id === 'square' && 'CSV exports from Square Dashboard. (Parser coming soon.)'}
                          {p.id === 'epos_now' && 'CSV exports from EPOS Now Back Office. (Parser coming soon.)'}
                          {p.id === 'zettle' && 'Raw-data Excel from Zettle. (Parser coming soon.)'}
                          {p.id === 'sumup' && 'SumUp Dashboard CSV — native API pull planned for Phase 3.'}
                          {p.id === 'manual' && 'No POS — daily takings entered manually.'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading providers…</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <Badge>Owner</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Invoice Processing
              </CardTitle>
              <p className="text-sm text-muted-foreground">Settings for Smart Invoice feature</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">Auto-spread delivery costs</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically distribute delivery charges across line items based on quantity
                    </p>
                  </div>
                  <Switch
                    checked={invoiceConfig.auto_spread_delivery}
                    onCheckedChange={handleToggleAutoSpreadDelivery}
                    disabled={savingInvoiceConfig || !currentLocation}
                    data-testid="auto-spread-delivery-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="tip-split-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Tip Split
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                How tips earned by your team are attributed on the Payroll page.
                Affects every employee in this organisation.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { value: 'by_logged_user', title: 'By logged user (default)', desc: 'Tips stay with whoever was logged into the POS when the order was placed. Simple, but inaccurate if staff share logins.' },
                  { value: 'pool_by_hours', title: 'Pool & split by hours worked', desc: 'All tips for the month are pooled and divided proportionally by each tip-eligible employee\'s hours.' },
                  { value: 'pool_by_orders', title: 'Pool & split by orders taken', desc: 'All tips for the month are pooled and divided by number of orders taken. Employees with 0 hours that month are excluded (filters out misattributed-login scenarios).' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${tipSplit.mode === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30'}`}
                    data-testid={`tip-split-option-${opt.value}`}
                  >
                    <input
                      type="radio"
                      name="tip-split-mode"
                      value={opt.value}
                      checked={tipSplit.mode === opt.value}
                      onChange={() => handleTipSplitChange(opt.value)}
                      disabled={savingTipSplit}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{opt.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                ))}
                <p className="text-xs text-muted-foreground pt-2">
                  Toggle <strong>tip eligibility per employee</strong> in <em>Settings → Employees</em> (defaults to ON). Useful for excluding chefs/managers from the pot.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Locations
                </CardTitle>
                <CardDescription>
                  Manage your café locations
                </CardDescription>
              </div>
              <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-location-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Location</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Location Name</Label>
                      <Input
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        placeholder="e.g. My Cafe - Kings Cross"
                        data-testid="new-location-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Input
                        value={newLocationAddress}
                        onChange={(e) => setNewLocationAddress(e.target.value)}
                        placeholder="e.g. 45 Euston Road, London N1 2AB"
                        data-testid="new-location-address"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setLocationDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateLocation} data-testid="save-location-btn">Create Location</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {locations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No locations yet</p>
              ) : (
                <div className="space-y-4">
                  {locations.map((loc) => {
                    // Format opening hours summary
                    const hasHours = loc.opening_hours && Object.keys(loc.opening_hours).length > 0;
                    let hoursText = 'Hours not set';
                    if (hasHours) {
                      const openDays = Object.entries(loc.opening_hours)
                        .filter(([_, h]) => h?.open && h?.close)
                        .map(([day, h]) => `${day.substring(0, 3).toUpperCase()}`);
                      if (openDays.length === 7) {
                        hoursText = 'Open every day';
                      } else if (openDays.length > 0) {
                        hoursText = `Open: ${openDays.join(', ')}`;
                      }
                    }
                    
                    return (
                      <div key={loc.id} className="flex items-center justify-between p-4 border rounded-md">
                        <div className="flex-1">
                          <p className="font-medium">{loc.name}</p>
                          <p className="text-sm text-muted-foreground">{loc.address}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {hoursText}
                          </p>
                          {providerCatalog && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-muted-foreground">POS:</span>
                              <Select
                                value={loc.pos_provider || '__inherit__'}
                                onValueChange={(v) => handleUpdateLocationPosProvider(loc.id, v === '__inherit__' ? '' : v)}
                                disabled={savingPosProvider}
                              >
                                <SelectTrigger className="h-8 text-xs w-[260px]" data-testid={`location-provider-${loc.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__inherit__">
                                    Use org default ({providerCatalog.providers[orgPosProvider]?.name})
                                  </SelectItem>
                                  {Object.values(providerCatalog.providers).map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {loc.pos_provider && (
                                <Badge variant="outline" className="text-[10px]">Override</Badge>
                              )}
                            </div>
                          )}
                          {loc.inbound_email && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground">Inbound email:</span>
                              <code
                                className="text-xs font-mono px-1.5 py-0.5 bg-muted rounded"
                                data-testid={`location-inbound-email-${loc.id}`}
                              >
                                {loc.inbound_email}
                              </code>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  navigator.clipboard.writeText(loc.inbound_email);
                                  toast.success('Copied');
                                }}
                                data-testid={`copy-inbound-email-${loc.id}`}
                                title="Copy inbound email address"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                              <span className="text-[10px] text-muted-foreground">
                                Forward supplier invoices &amp; chase emails here.
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {loc.id === currentLocation?.id && (
                            <Badge variant="secondary">Current</Badge>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditLocation(loc)}
                            data-testid={`edit-location-${loc.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Edit Location Dialog */}
          <Dialog open={editLocationDialogOpen} onOpenChange={setEditLocationDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Location</DialogTitle>
                <DialogDescription>Update the location details and opening hours</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Location Name</Label>
                  <Input
                    value={editLocationName}
                    onChange={(e) => setEditLocationName(e.target.value)}
                    placeholder="e.g. Moon Cafe - Kings Cross"
                    data-testid="edit-location-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={editLocationAddress}
                    onChange={(e) => setEditLocationAddress(e.target.value)}
                    placeholder="e.g. 45 Euston Road, London N1 2AB"
                    data-testid="edit-location-address"
                  />
                </div>
                
                <Separator />
                
                {/* Opening Hours */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Opening Hours
                  </Label>
                  <div className="space-y-2">
                    {DAYS_OF_WEEK.map(({ key, label }) => {
                      const dayHours = editOpeningHours[key];
                      const isOpen = !!dayHours;
                      
                      return (
                        <div key={key} className="flex items-center gap-3">
                          <div className="w-24 flex items-center gap-2">
                            <Switch
                              checked={isOpen}
                              onCheckedChange={() => toggleDayOpen(key)}
                              id={`day-${key}`}
                            />
                            <Label htmlFor={`day-${key}`} className="text-sm cursor-pointer">
                              {label.substring(0, 3)}
                            </Label>
                          </div>
                          {isOpen ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                type="time"
                                value={dayHours?.open || '09:00'}
                                onChange={(e) => updateDayHours(key, 'open', e.target.value)}
                                className="w-28 text-sm"
                              />
                              <span className="text-muted-foreground">to</span>
                              <Input
                                type="time"
                                value={dayHours?.close || '17:00'}
                                onChange={(e) => updateDayHours(key, 'close', e.target.value)}
                                className="w-28 text-sm"
                              />
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Closed</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditLocationDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateLocation} data-testid="update-location-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6" data-testid="team-settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Add Team Member
              </CardTitle>
              <CardDescription>
                Add someone to your organisation. They must have already registered an account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="team.member@email.com"
                  value={addMemberEmail}
                  onChange={(e) => setAddMemberEmail(e.target.value)}
                  className="flex-1"
                  data-testid="add-member-email"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
                />
                <Select value={addMemberRole} onValueChange={setAddMemberRole}>
                  <SelectTrigger className="w-full sm:w-36" data-testid="add-member-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddMember} disabled={addingMember || !addMemberEmail.trim()} data-testid="add-member-btn">
                  {addingMember ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members
              </CardTitle>
              <CardDescription>
                People in your organisation and their roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {teamLoading ? (
                <div className="flex items-center gap-2 py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading team...</span>
                </div>
              ) : teamMembers.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">No team members found</p>
              ) : (
                <div className="space-y-3">
                  {teamMembers.map((member) => {
                    const isCurrentUser = member.id === user?.id;
                    const isOwner = member.role === 'owner';
                    return (
                      <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`team-member-${member.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                            <span className="text-sm font-medium">
                              {(member.name || member.email || '?').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {member.name || member.email}
                              {isCurrentUser && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isOwner || isCurrentUser ? (
                            <Badge className={`${ROLE_COLORS[member.role]} border text-xs`}>
                              {ROLE_LABELS[member.role]}
                            </Badge>
                          ) : (
                            <Select value={member.role} onValueChange={(val) => handleChangeRole(member.id, val)}>
                              <SelectTrigger className="w-28 h-8 text-xs" data-testid={`role-select-${member.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="manager">Manager</SelectItem>
                                <SelectItem value="staff">Staff</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {!isOwner && !isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(member.id)}
                              disabled={removingMemberId === member.id}
                              className="text-muted-foreground hover:text-destructive"
                              data-testid={`remove-member-${member.id}`}
                            >
                              {removingMemberId === member.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserMinus className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Telegram Tab */}
        <TabsContent value="telegram" className="space-y-6" data-testid="telegram-settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Telegram Connection
              </CardTitle>
              <CardDescription>
                Connect your Telegram account to receive weekly reports, log expenses, and upload receipts on the go
              </CardDescription>
            </CardHeader>
            <CardContent>
              {telegramLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Checking connection...</span>
                </div>
              ) : telegramStatus.connected ? (
                /* Connected state */
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-emerald-500/5 border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <Send className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">Connected</p>
                          <Badge variant="outline" className="border-emerald-500/30 text-emerald-600">Active</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {telegramStatus.telegram_display_name || `ID: ${telegramStatus.telegram_user_id}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisconnectTelegram}
                      disabled={disconnecting}
                      data-testid="disconnect-telegram-btn"
                    >
                      {disconnecting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Unlink className="w-4 h-4 mr-1" />
                      )}
                      Disconnect
                    </Button>
                  </div>

                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">What you can do via Telegram:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Log expenses: "Owe Funnybones 450"</li>
                      <li>• Track payments: "Paid Mission Coffee 200"</li>
                      <li>• Upload receipt photos for OCR processing</li>
                      <li>• Get weekly P&L summary: /weekly</li>
                      <li>• Check outstanding: "What do I owe?"</li>
                    </ul>
                  </div>

                  {/* Notification Preferences */}
                  <Card className="border-dashed" data-testid="notification-prefs-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Notification Preferences</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Weekly P&L Report</p>
                          <p className="text-xs text-muted-foreground">Receive your weekly summary every Monday</p>
                        </div>
                        <Switch
                          checked={notifPrefs.weekly_report}
                          onCheckedChange={(val) => handleToggleNotifPref('weekly_report', val)}
                          disabled={savingPrefs}
                          data-testid="toggle-weekly-report"
                        />
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Document Expiry Alerts</p>
                          <p className="text-xs text-muted-foreground">Get notified when documents are about to expire</p>
                        </div>
                        <Switch
                          checked={notifPrefs.document_expiry_alerts}
                          onCheckedChange={(val) => handleToggleNotifPref('document_expiry_alerts', val)}
                          disabled={savingPrefs}
                          data-testid="toggle-doc-expiry-alerts"
                        />
                      </div>
                      {notifPrefs.document_expiry_alerts && (
                        <div className="flex items-center justify-between pl-4 border-l-2 border-muted">
                          <div>
                            <p className="text-sm text-muted-foreground">Alert me this many days before expiry</p>
                          </div>
                          <select
                            className="text-sm border rounded-md px-2 py-1 bg-background"
                            value={notifPrefs.expiry_alert_days}
                            onChange={(e) => handleToggleNotifPref('expiry_alert_days', parseInt(e.target.value))}
                            disabled={savingPrefs}
                            data-testid="expiry-alert-days-select"
                          >
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                          </select>
                        </div>
                      )}
                      <Separator />
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Fixed Cost Payment Alerts</p>
                          <p className="text-xs text-muted-foreground">Get notified about upcoming utility bills and recurring payments</p>
                        </div>
                        <Switch
                          checked={notifPrefs.fixed_cost_alerts}
                          onCheckedChange={(val) => handleToggleNotifPref('fixed_cost_alerts', val)}
                          disabled={savingPrefs}
                          data-testid="toggle-fixed-cost-alerts"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                /* Not connected state */
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg border-dashed">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Send className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">Not connected</p>
                        <p className="text-sm text-muted-foreground">Link your Telegram to get started</p>
                      </div>
                    </div>
                    {!linkCode && (
                      <Button
                        onClick={handleGenerateTelegramCode}
                        disabled={generatingCode}
                        data-testid="connect-telegram-btn"
                      >
                        {generatingCode ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Link className="w-4 h-4 mr-1" />
                        )}
                        Connect Telegram
                      </Button>
                    )}
                  </div>

                  {linkCode && (
                    <div className="p-4 border rounded-lg bg-blue-500/5 border-blue-500/20 space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500">Step 1</Badge>
                        <span className="text-sm">Open our Telegram bot: <strong>{process.env.REACT_APP_TELEGRAM_BOT_NAME || '@cafe_intelligence_bot'}</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500">Step 2</Badge>
                        <span className="text-sm">Send this command:</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-background border rounded-md font-mono text-lg tracking-widest text-center"
                              data-testid="telegram-connect-code">
                          /connect {linkCode}
                        </code>
                        <Button variant="outline" size="sm" onClick={handleCopyCommand} data-testid="copy-code-btn">
                          {codeCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Code expires in 5 minutes. After connecting, refresh this page to see the status update.
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Why connect Telegram?</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Receive your weekly P&L summary automatically</li>
                      <li>• Log expenses and payments with a quick message</li>
                      <li>• Snap and send receipt photos for instant OCR</li>
                      <li>• Check what you owe without opening the app</li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Employees
                </CardTitle>
                <CardDescription>
                  Team members at {currentLocation?.name}
                </CardDescription>
              </div>
              <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-employee-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Employee
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Employee</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={newEmployee.name}
                        onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                        data-testid="new-employee-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Input
                        value={newEmployee.role}
                        onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}
                        placeholder="e.g. Barista, Manager"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hourly Rate (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newEmployee.hourly_rate}
                        onChange={(e) => setNewEmployee({...newEmployee, hourly_rate: e.target.value})}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateEmployee} data-testid="save-employee-btn">Add Employee</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No employees added</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Hourly Rate</TableHead>
                      <TableHead className="text-center">Tips Eligible</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.role || '-'}</TableCell>
                        <TableCell className="text-right mono-num">
                          {emp.hourly_rate ? `£${emp.hourly_rate.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={emp.tips_eligible !== false}
                            onCheckedChange={async (checked) => {
                              try {
                                await api.put(`/employees/${emp.id}`, { tips_eligible: checked });
                                setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, tips_eligible: checked } : e));
                              } catch {
                                toast.error('Failed to update tip eligibility');
                              }
                            }}
                            data-testid={`tips-eligible-${emp.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Suppliers
                </CardTitle>
                <CardDescription>
                  Vendor contacts for {currentLocation?.name}
                </CardDescription>
              </div>
              <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-supplier-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Supplier
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Supplier</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={newSupplier.name}
                        onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                        data-testid="new-supplier-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Input
                        value={newSupplier.category}
                        onChange={(e) => setNewSupplier({...newSupplier, category: e.target.value})}
                        placeholder="e.g. Coffee, Dairy, Bakery"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email</Label>
                      <Input
                        type="email"
                        value={newSupplier.contact_email}
                        onChange={(e) => setNewSupplier({...newSupplier, contact_email: e.target.value})}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateSupplier} data-testid="save-supplier-btn">Add Supplier</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {suppliers.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No suppliers added</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Contact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.map((sup) => (
                      <TableRow key={sup.id}>
                        <TableCell className="font-medium">{sup.name}</TableCell>
                        <TableCell>
                          {sup.category && <Badge variant="secondary">{sup.category}</Badge>}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{sup.contact_email || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Menu Items Tab */}
        <TabsContent value="menu" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coffee className="w-5 h-5" />
                Menu Items
              </CardTitle>
              <CardDescription>
                Items tracked from your POS at {currentLocation?.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {menuItems.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No menu items yet. Import a sales report from your POS to add items.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {menuItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>
                          {item.category && <Badge variant="secondary">{item.category}</Badge>}
                        </TableCell>
                        <TableCell className="text-right mono-num">
                          {item.price ? `£${item.price.toFixed(2)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features" className="space-y-6" data-testid="features-settings">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Feature Access</CardTitle>
              <CardDescription>Enable or disable features for your organization. Changes apply company-wide to all team members.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Board Meeting</p>
                  <p className="text-xs text-muted-foreground">AI section heads analyse your business and a CEO mentor suggests strategy</p>
                </div>
                <Switch
                  checked={features.board_meeting}
                  onCheckedChange={(val) => handleToggleFeature('board_meeting', val)}
                  disabled={featuresLoading}
                  data-testid="toggle-board-meeting"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">AI Projects</p>
                  <p className="text-xs text-muted-foreground">AI-generated project plans from business goals</p>
                </div>
                <Switch
                  checked={features.ai_projects}
                  onCheckedChange={(val) => handleToggleFeature('ai_projects', val)}
                  disabled={featuresLoading}
                  data-testid="toggle-ai-projects"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">AI Chat</p>
                  <p className="text-xs text-muted-foreground">Business advisor chat with access to your data</p>
                </div>
                <Switch
                  checked={features.ai_chat}
                  onCheckedChange={(val) => handleToggleFeature('ai_chat', val)}
                  disabled={featuresLoading}
                  data-testid="toggle-ai-chat"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Smart Invoice</p>
                  <p className="text-xs text-muted-foreground">AI-powered invoice OCR and ingredient mapping</p>
                </div>
                <Switch
                  checked={features.smart_invoice}
                  onCheckedChange={(val) => handleToggleFeature('smart_invoice', val)}
                  disabled={featuresLoading}
                  data-testid="toggle-smart-invoice"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Email Integration</p>
                  <p className="text-xs text-muted-foreground">Gmail connection for processing supplier emails and payment chases</p>
                </div>
                <Switch
                  checked={features.email}
                  onCheckedChange={(val) => handleToggleFeature('email', val)}
                  disabled={featuresLoading}
                  data-testid="toggle-email"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="modules" className="space-y-6" data-testid="modules-settings">
          <ModulesTabContent />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-6" data-testid="integrations-settings">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Integrations</h2>
            <p className="text-sm text-muted-foreground">
              Connect Tillytics to your point-of-sale and other systems for automatic data sync.
            </p>
          </div>
          <SquareIntegrationCard />
        </TabsContent>
      </Tabs>

      {/* Data Management Section */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions that affect your data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border border-destructive/30 rounded-lg bg-destructive/5">
            <div>
              <h4 className="font-medium">Reset All Data</h4>
              <p className="text-sm text-muted-foreground">
                Delete all revenue, expenses, menu items, recipes, ingredients, and reports for {currentLocation?.name}. 
                User accounts and location settings will be preserved.
              </p>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setResetDialogOpen(true)}
              data-testid="reset-data-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Reset Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reset Data Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Reset All Data
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete all operational data for <strong>{currentLocation?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <h4 className="font-medium text-destructive mb-2">The following will be deleted:</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>All revenue records and POS sales data</li>
                <li>All expenses, receipts, and payroll data</li>
                <li>All menu items and product mix data</li>
                <li>All recipes and ingredients</li>
                <li>All staff shifts and transactions</li>
                <li>All notes, reports, and AI conversations</li>
              </ul>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">The following will be preserved:</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Your user account and login</li>
                <li>Organization and location settings</li>
                <li>Employee records (for re-importing)</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label>Type <strong>RESET ALL DATA</strong> to confirm:</Label>
              <Input
                value={resetConfirmation}
                onChange={(e) => setResetConfirmation(e.target.value)}
                placeholder="RESET ALL DATA"
                className="font-mono"
                data-testid="reset-confirmation-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetDialogOpen(false);
              setResetConfirmation('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleResetData}
              disabled={resetConfirmation !== 'RESET ALL DATA' || resetting}
              data-testid="confirm-reset-btn"
            >
              {resetting ? 'Resetting...' : 'Reset All Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;

// ── Modules tab ─────────────────────────────────────────────────────────────
const ModulesTabContent = () => {
  const { catalogue, enabledKeys, toggleModule, loading } = useModules();
  const { user } = useAuth();
  const [savingKey, setSavingKey] = useState(null);
  const role = (user?.role || '').toLowerCase();
  const canToggle = role === 'owner' || role === 'manager' || role === 'admin';
  const enabledSet = new Set(enabledKeys);

  const handleToggle = async (key, enabled) => {
    setSavingKey(key);
    try {
      await toggleModule(key, enabled);
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} successfully.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update module');
    } finally {
      setSavingKey(null);
    }
  };

  const tierBadge = (tier) => {
    const colors = {
      free: 'bg-gray-100 text-gray-700',
      starter: 'bg-blue-100 text-blue-700',
      pro: 'bg-purple-100 text-purple-700',
      franchise: 'bg-amber-100 text-amber-700',
    };
    return (
      <span className={`text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded ${colors[tier] || colors.pro}`}>
        {tier}
      </span>
    );
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading modules…</p>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Modules</CardTitle>
        <CardDescription>
          Choose which areas of Tillytics this organisation uses. Disabled modules are hidden from
          the sidebar and the data is preserved — you can re-enable any module at any time. Free
          during preview; will be tied to your subscription tier when billing launches.
          {!canToggle && (
            <span className="block mt-2 text-amber-600">
              Only owners and admins can change which modules are active.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {catalogue.map((m) => {
          const checked = enabledSet.has(m.key) || m.always_on;
          const disabled = m.always_on || m.coming_soon || !canToggle || savingKey === m.key;
          return (
            <div
              key={m.key}
              className="flex items-center justify-between p-3 rounded-md border hover:bg-muted/30 transition-colors"
              data-testid={`module-row-${m.key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{m.display_name}</span>
                  {tierBadge(m.tier)}
                  {m.always_on && (
                    <span className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                      always on
                    </span>
                  )}
                  {m.coming_soon && (
                    <span className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                      coming soon
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>
              </div>
              <Switch
                checked={checked}
                onCheckedChange={(v) => handleToggle(m.key, v)}
                disabled={disabled}
                data-testid={`module-toggle-${m.key}`}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
